import { useInstance } from '@meraki-internal/react-dependency-injection';
import { IQueryParamDrivenDrawerContentProvider } from '../components/query-param-driver-drawer/IQueryParamDrivenDrawerContentProvider';
import { QueryParamDrivenDrawerHelper } from '../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { CareMapState } from '../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { useRerenderOnUploadChange } from '../notes/generic-notes/attachments/useRerenderOnUploadChange';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { AlertPresenter } from '../AlertBinder';
import { useSubscriptionSync } from '../support/state/useSubscriptionV2';
import { QueryParamDrivenDrawerPage } from '../components/CareMapDrawerPage';
import { IonButton } from '@ionic/react';
import { ActiveCaremapNoteCreator } from '../notes/add-note/ActiveCaremapNoteCreator';
import { TemplateProvider } from '../notes/generic-notes/note-templates/TemplateProvider';
import { useState } from 'react';

interface IAddCommunityPostDrawerProps {
    trackingFrom?: string;
    templateId?: string;
};

export class AddCommunityPostWithQueryParamDrawer implements IQueryParamDrivenDrawerContentProvider<IAddCommunityPostDrawerProps> {
    static inject = () => [QueryParamDrivenDrawerHelper];
    constructor(private drawerHelper: QueryParamDrivenDrawerHelper<IAddCommunityPostDrawerProps>){}

    mode = 'add-community-post';

    Component = AddNoteDrawerView;

    open = (props?: IAddCommunityPostDrawerProps) => {
        this.drawerHelper.open({ mode: this.mode, ...props });
    };
};

const AddNoteDrawerView: React.FC = () => {
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    useRerenderOnUploadChange();

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const alert = useInstance(AlertPresenter);
    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);
    const templates = useInstance(TemplateProvider).getTemplates({section: 'patient'});

    const { templateId } = drawerHelper.getDrawerParams();
    const template = templates.find(t => t.templateId === templateId);
    const focusLocation = template?.focusLocation;

    const [manuallySelectedTemplateLabel, setManuallySelectedTemplateLabel] = useState<string | undefined>();

    const noteVM = useSubscriptionSync(() => {
        return notesFactory.createFormForNewNote({section: 'patient'}, {isPrivate: false, html: template?.body || ''});
    });

    const saveAndClose = async () => {
        try {
            const { trackingFrom } = drawerHelper.getDrawerParams();
            await noteVM.save({
                trackingEvent: 'Community Post Added',
                trackingTemplateLabel: template?.label || manuallySelectedTemplateLabel,
                // hack as these template buttons / story is short term
                trackingFrom: template ? 'Community Page Templates' : trackingFrom
            });
            drawerHelper.close();
        }
        catch (err: any){
            alert.showAndLogError(err);
        }
    };

    return (
        <QueryParamDrivenDrawerPage
            title="Add Post"
            hasBack={false}
            data-type="note-editor"
            buttons={(
                <>
                    <IonButton
                        data-type="save-button"
                        disabled={!noteVM.canSave()}
                        onClick={saveAndClose}>Save</IonButton>
                    <IonButton data-type="cancel-button" fill="outline" onClick={() => {
                        drawerHelper.close();
                    }}>Cancel</IonButton>
                </>
            )}
        >
            <ActiveCaremapNoteCreator
                noteVM={noteVM}
                includeTemplates
                hasPrivateCheckbox={false}
                focusLocation={focusLocation}
                templateSelected={setManuallySelectedTemplateLabel}
            />
        </QueryParamDrivenDrawerPage>
    );
};
